import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig } from '@/mocks/siteConfig';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt' || !honeypotValue) {
        urlEncoded.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rfmmvnahb7382ovlp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = parsed?.code as string | undefined;
      const metaMsg = (parsed?.meta as Record<string, unknown>)?.message as string || '';
      const serverMsg = metaMsg || (parsed?.message as string) || '';

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${siteConfig.name}`,
            description: `了解${siteConfig.name}的品牌故事、专业团队和服务理念`,
            url: `${siteConfig.url}about`,
            about: {
              '@type': 'Organization',
              name: siteConfig.name,
              description: siteConfig.tagline,
              url: siteConfig.url,
              address: {
                '@type': 'PostalAddress',
                addressLocality: siteConfig.address.locality,
                addressRegion: siteConfig.address.region,
                addressCountry: siteConfig.address.country,
              },
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: siteConfig.contact.phone,
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
            },
          }),
        }}
      />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">
              关于我们
            </h1>
          </div>
        </div>

        <section className="py-14 md:py-18">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {siteConfig.name}成立于2020年，是专注于中华传统文化与现代生活相结合的专业开运平台。我们汇聚了多位资深风水师、命理师和开运顾问，致力于为每一位追求美好生活的用户提供专业、可靠的开运知识和产品推荐。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的使命是让古老的东方智慧以现代、科学的方式走进每个人的生活。无论是家居风水布局、开运饰品选择，还是命理运势解读，我们都力求用通俗易懂的方式，帮助用户理解和运用这些传统智慧。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们相信，好运并非偶然。通过正确的方法和积极的心态，每个人都能够吸引更多的好运进入生活。{siteConfig.name}愿成为您追寻好运路上的忠实伙伴。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden h-72 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20study%20room%20with%20traditional%20decor%2C%20calligraphy%20scrolls%20on%20wall%2C%20wooden%20desk%20with%20tea%20set%2C%20warm%20soft%20lighting%20from%20paper%20lantern%2C%20scholarly%20atmosphere%2C%20cream%20walls%2C%20editorial%20interior%20photography&width=600&height=500&seq=about-brand&orientation=portrait"
                  alt="开运 com 品牌"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
              口碑声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">50万+</div>
                <p className="text-sm text-foreground-500">累计服务用户</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">98%</div>
                <p className="text-sm text-foreground-500">用户好评率</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">1000+</div>
                <p className="text-sm text-foreground-500">原创开运文章</p>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "按照开运 com 的家居风水指南重新布置了客厅，明显感觉家里的氛围变得更温馨和睦了，事业上最近也有了新的机会！真心推荐给大家。"
                </p>
                <p className="text-xs text-foreground-400">— 李女士，上海</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "在开运 com 上学习了很多水晶知识，按照推荐入手了黄水晶手链，佩戴后感觉整个人的状态和财运都在慢慢变好。内容很专业很靠谱。"
                </p>
                <p className="text-xs text-foreground-400">— 王先生，深圳</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "生肖运势解析写得非常详细，2026年的运势分析帮我在年初做了很多正确的决策。网站内容更新快，每天都会来看看。"
                </p>
                <p className="text-xs text-foreground-400">— 张女士，北京</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "非常喜欢每日幸运色功能，已经养成了每天早上查看幸运色的习惯。在重要场合按照推荐穿搭，感觉自信多了！"
                </p>
                <p className="text-xs text-foreground-400">— 陈先生，广州</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  联系方式
                </h3>
                <ul className="flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电话</p>
                      <p className="text-sm text-foreground-500">{siteConfig.contact.phone}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">邮箱</p>
                      <p className="text-sm text-foreground-500">{siteConfig.contact.email}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">地址</p>
                      <p className="text-sm text-foreground-500">{siteConfig.address.full}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信</p>
                      <p className="text-sm text-foreground-500">{siteConfig.contact.wechat}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  id="about-contact-form"
                  data-readdy-form
                  className="flex flex-col gap-4"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer self-start"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}